import React, { useEffect, useMemo, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { addDays, format } from 'date-fns'
import { getCookie, useEffectOnce, usePathParam, formatCurrency } from '@chilecompra/react-kit'
import { Card, Spinner, Toast, Typography } from '@chilecompra/react-kit/components'

import {
  CURRENCY_DECIMALS_DICTIONARY,
  CURRENCY_SIGNS_DICTIONARY,
  FOREIGN_PROVIDER_DICTIONARY,
  QUOTE_STATUSES_DICTIONARY,
  QUOTE_STATUSES_PROVIDER
} from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { topPage } from '../../modules/utils/topPage'
import { validateMaximumAmountUtm } from '../../modules/utils/validations'
import { getCookieFromDomain } from '../../modules/cookies.js'
import { inIframe } from '../../modules/utils/inIframe.js'
import { roundHalfUp } from '../../modules/utils/formatters.js'

import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

import AttachFiles from '../../components/Custom/AttachFiles'
import AttachedFiles from '../../components/Custom/AttachedFiles'
import AttachedFilesBlue from '../../components/Custom/AttachedFilesBlue'
import DownloadComprobanteLink from '../../components/Custom/DownloadComprobanteLink'
import Errors from '../../components/Custom/Errors'
import SummaryBid from '../../components/Custom/SummaryBid'

import CancelBid from './QuoteBidSeller.cancelBid'
import QuoteBidSellerAmount from './QuoteBidSeller.amount'
import QuoteBidSellerDetails from './QuoteBidSeller.details'
import QuoteBidSellerDispatchValue from './QuoteBidSeller.dispatchValue'
import QuoteBidSellerFooter from './QuoteBidSeller.footer'
import QuoteBidSellerProductsList from './QuoteBidSeller.productsList'

import {
  onHideErrorsSendSellerQuoteDialogSuccess,
  onShowSendSellerQuoteDialog
} from '../SendSellerQuoteDialog/SendSellerQuoteDialog.actions'
import { onCancelBidShowDialogSuccess } from '../CancelBidSellerDialog/CancelBidSellerDialog.actions'
import {
  onAddOfferSuccess,
  onDeleteFileThunk,
  onGetDownloadFileThunk,
  onGetTaxationThunk,
  onHideErrorsQuoteBidSellerSuccess,
  onSaveQuoteSellerSuccess,
  onShowErrorAmountSuccess,
  onUploadFileSellerThunk
} from './QuoteBidSeller.actions'
import {
  onApplyPriorizedBuyer,
  onClearMsgOrderPrioritized,
  onGetPrioritizedSupplierThunk,
  onGetSupplierHabilityV3Thunk
} from '../QuoteSearchHeadingFilter/QuoteSearchHeadingFilters.actions.js'

import { Divider, GridContainer, GridItem, Root, Strong, Tag } from './QuoteBidSeller.styles'

/**
 * The Quote Bid's container.
 */
const QuoteBidSellerContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userInfo = Object.fromEntries(new URLSearchParams(getCookieFromDomain('user_info')))

  const {
    attachedFiles,
    errors: errorsQuoteBidSeller,
    loading: loadingFile,
    addOffer,
    taxationOptions
  } = useSelector(state => state.quoteBidSeller)
  const {
    initData: { exchangeRates, maxAmountsUTMs }
  } = useSelector(state => state.quoteStepOne)
  const { basicInfo, bidsSeller, error, loading, products, statusCallAgile } = useSelector(
    state => state.quoteBasicInformation
  )
  const {
    errors: errorsSendSellerQuoteDialog,
    loading: loadingSendSeller,
    open: openSend
  } = useSelector(state => state.sendSellerQuoteDialog)
  const { open: openCancel } = useSelector(state => state.cancelBidSellerDialog)
  const { supplierHability, supplierPrioritized } = useSelector(state => state.quoteSearchHeadingFilters)
  const {
    authInfo: { orgTaxID }
  } = useSelector(state => state.authorizationProvider)

  const quoteId = usePathParam('quoteId')
  const { isSeller } = useAuthorization()
  const isInIframe = useMemo(() => inIframe(), [])

  const [percentage, setPercentage] = useState(19)
  const [netWorth, setNetWorth] = useState(0)
  const [taxation, setTaxation] = useState(0)
  const [taxWhithHolding, setTaxWithHolding] = useState(false)
  const [amount, setAmount] = useState(0)
  const [dispatchValue, setDispatchValue] = useState(0)
  const [productsUpdate, setProductsUpdate] = useState({})
  const [disabledSubmit, setDisabledSubmit] = useState(true)
  const [description, setDescription] = useState('')
  const [effectiveDate, setEffectiveDate] = useState(format(addDays(new Date(), 30), 'yyyy-MM-dd HH:mm:ss'))
  const [taxationOption, setTaxationOption] = useState(1)
  const [disabledSupplierHability, setDisabledSupplierHability] = useState(false)
  const [refreshingEmt, setRefreshingEmt] = useState(false)

  const currentExchangeRateUTM = exchangeRates.find(exchangeRate => exchangeRate.value === 'UTM')
  const maximumAmount = formatCurrency(
    parseInt(parseInt(currentExchangeRateUTM?.clpAmount || 0, 10) * parseInt(maxAmountsUTMs, 10), 10)
  )
  const publishedSeller = isSeller && basicInfo.statusId === 2
  const utmToClpAmount =
    parseInt(exchangeRates?.find(exchangeRate => exchangeRate.value === 'UTM')?.clpAmount, 10) *
    parseInt(maxAmountsUTMs, 10)

  const setSubtotal = (subtotal, productId, included, unitValue) => {
    setProductsUpdate({
      ...productsUpdate,
      [productId]: {
        ...productsUpdate[productId],
        subtotal,
        unitValue,
        included
      }
    })
  }
  const typeNumber = value => {
    if (basicInfo.currency === 'CLP') {
      return parseInt(value, 10)
    }

    return parseFloat(value)
  }
  const formattedFloat = value => {
    if (basicInfo.currency === 'CLP') {
      return value
    }

    return value
      .toString()
      .replace(/[^0-9.,]/g, '')
      .replace(',', '.')
  }
  const dispatchValueValidate = value => {
    if (value !== '' && value > 0) {
      return value
    }
    return 0
  }
  const calculateNetWorth = () => {
    const result = Object.values(productsUpdate)
      .map(product => (product.subtotal === '' ? 0 : typeNumber(formattedFloat(product.subtotal))))
      .reduce((previous, current) => previous + current, 0)
    const sumDispatch = dispatchValueValidate(dispatchValue)

    if (isNaN(result)) {
      setNetWorth(0)
      return
    }

    const sumNetWorth = typeNumber(result) + typeNumber(sumDispatch)
    const roundingUpNetWorth = roundHalfUp(sumNetWorth, CURRENCY_DECIMALS_DICTIONARY[basicInfo.currency])

    setNetWorth(roundingUpNetWorth)
  }
  const calculateDispatchValue = varDispatchValue => setDispatchValue(varDispatchValue)
  const calculateTaxation = () => {
    if (basicInfo.currency === CURRENCY_SIGNS_DICTIONARY.CLP) {
      const netInt = parseInt(netWorth, 10)
      const sumTaxInt = taxWhithHolding ? netInt / (1 - percentage / 100) - netInt : (netInt * percentage) / 100
      const roundingUpTaxInt = roundHalfUp(sumTaxInt, CURRENCY_DECIMALS_DICTIONARY[basicInfo.currency])

      setTaxation(roundingUpTaxInt)
      return
    }

    const netFloat = parseFloat(formattedFloat(netWorth))
    const sumTax = taxWhithHolding ? netFloat / (1 - percentage / 100) - netFloat : (netFloat * percentage) / 100

    const roundingUpTax = roundHalfUp(sumTax, CURRENCY_DECIMALS_DICTIONARY[basicInfo.currency])

    setTaxation(roundingUpTax)
  }
  const calculateAmount = () => {
    if (basicInfo.currency === CURRENCY_SIGNS_DICTIONARY.CLP) {
      const sumAmountInt = parseInt(netWorth, 10) + parseInt(taxation, 10)
      const roundingUpAmountInt = roundHalfUp(sumAmountInt, CURRENCY_DECIMALS_DICTIONARY[basicInfo.currency])

      setAmount(roundingUpAmountInt)
      return
    }

    const sumAmount = parseFloat(formattedFloat(netWorth)) + parseFloat(formattedFloat(taxation))
    const roundingUpAmount = roundHalfUp(sumAmount, CURRENCY_DECIMALS_DICTIONARY[basicInfo.currency])

    setAmount(roundingUpAmount)
  }
  const percentageTaxation = taxationValue => {
    setTaxationOption(taxationValue)
    const option = taxationOptions.find(item => item.value === taxationValue)
    if (!option) {
      return
    }

    setPercentage(option.percentage)
    setTaxWithHolding(option.withHolding === 1)
  }

  const handleDisabledSubmit = () => {
    const products = Object.values(productsUpdate).find(product => {
      return (product.subtotal === 0 || product.subtotal === '') && product.included
    })

    return !(!products && netWorth !== 0 && description !== '')
  }
  const handleSendQuote = async () => {
    if (supplierHability?.status === QUOTE_STATUSES_PROVIDER.ENABLED) {
      setRefreshingEmt(true)
      try {
        const refreshedPrioritized = await dispatch(onGetPrioritizedSupplierThunk({ orgTaxID }))
        const isNoEMT = refreshedPrioritized ? refreshedPrioritized.isNoEMT : supplierPrioritized?.isNoEMT

        if (isNoEMT && !statusCallAgile?.isSecondCall) return
      } finally {
        setRefreshingEmt(false)
      }

      if (!validateMaximumAmountUtm({ currency: basicInfo.currency, amount, utmToClpAmount, exchangeRates })) {
        return dispatch(onShowErrorAmountSuccess())
      }

      const quoteBidSeller = {
        amount,
        description,
        dispatchValue,
        netWorth,
        quoteId,
        taxation,
        taxationOption,
        currency: basicInfo.currency,
        dateClose: effectiveDate,
        products: Object.values(productsUpdate).map(product => ({
          included: product.included ? 1 : 0,
          productId: product.id,
          productCode: product.productCode,
          quantity: product.quantity,
          subtotal: product.subtotal === '' ? 0 : product.subtotal,
          unitValue: product.unitValue
        }))
      }

      batch(() => {
        dispatch(onSaveQuoteSellerSuccess({ quoteBidSeller }))
        dispatch(onShowSendSellerQuoteDialog({ showAbilityAffidavit: false }))
      })
    }
  }
  const handleUploadFile = file => {
    dispatch(
      onUploadFileSellerThunk({
        ipUsuario: userInfo.host || '',
        quoteCode: quoteId,
        file
      })
    )
  }
  const handleDeleteFile = fileId =>
    dispatch(onDeleteFileThunk({ fileId, quoteCode: quoteId, ipUsuario: userInfo.host || '' }))
  const handleDownloadFile = ({ id, name }) => dispatch(onGetDownloadFileThunk({ id, name }))
  const handleBackSearch = () => {
    batch(() => {
      dispatch(onHideErrorsQuoteBidSellerSuccess())
      dispatch(onHideErrorsSendSellerQuoteDialogSuccess())
      dispatch(onAddOfferSuccess({ addOffer: false }))
    })
    const query = getCookie('search')
    navigate({ pathname: routes.search, search: query.toString() }, { replace: true })
  }
  const handleCancelBidShowDialog = bidId => dispatch(onCancelBidShowDialogSuccess({ bidId }))
  const handleAddOffer = () => dispatch(onAddOfferSuccess({ addOffer: true }))
  const areThereProductsIncluded = useMemo(
    () => Object.values(productsUpdate).some(product => product.included),
    [productsUpdate]
  )

  useEffectOnce(() => {
    batch(() => {
      dispatch(onClearMsgOrderPrioritized())
      dispatch(onHideErrorsQuoteBidSellerSuccess())
      dispatch(onGetTaxationThunk())

      if (isSeller) {
        dispatch(
          onGetPrioritizedSupplierThunk({
            orgTaxID,
            callback: supplierHability
              ? undefined
              : isForeign => {
                  dispatch(
                    onGetSupplierHabilityV3Thunk({
                      orgTaxID,
                      isForeign: isForeign ? FOREIGN_PROVIDER_DICTIONARY.FOREIGN : FOREIGN_PROVIDER_DICTIONARY.NATIONAL
                    })
                  )
                }
          })
        )
      } else {
        dispatch(onApplyPriorizedBuyer())
      }
    })
  })

  useEffect(() => {
    setProductsUpdate(() => products.reduce((dictionary, product) => ({ ...dictionary, [product.id]: product }), {}))
  }, [products])

  useEffect(() => {
    /**
     * You can bid if you are enabled, but it also depends on the type of EMT company:
     * if it is not EMT, you can only bid in second call
     * If it is EMT, you can bid if you are qualified.
     *
     */
    setDisabledSupplierHability(
      !supplierHability?.isEnabled || (supplierPrioritized?.isNoEMT && !statusCallAgile?.isSecondCall)
    )
  }, [supplierHability, supplierPrioritized, statusCallAgile])

  useEffect(() => {
    calculateNetWorth()
    setDisabledSubmit(handleDisabledSubmit())
  }, [productsUpdate, dispatchValue, netWorth, description])

  useEffect(() => {
    calculateTaxation()
  }, [netWorth, percentage])

  useEffect(() => {
    calculateAmount()
  }, [taxation, netWorth])

  useEffect(() => {
    topPage(isInIframe, openSend)
  }, [openSend])

  useEffect(() => {
    topPage(isInIframe, openCancel)
  }, [openCancel])

  if (!publishedSeller) {
    return null
  }

  if (bidsSeller && bidsSeller.length > 0 && !addOffer) {
    return (
      <GridContainer margin="64px 0">
        {bidsSeller.map(bidSeller => (
          <Card color="blank" elevation="2" key={bidSeller.id} enableBorder padding="0 32px" margin="0 0 8px">
            <SummaryBid
              amount={bidSeller.amount}
              detail={bidSeller.detail}
              dispatchValue={bidSeller.dispatchValue}
              downloadAction={<DownloadComprobanteLink cotizacionId={bidSeller.id} label="Descargar comprobante" />}
              effectiveDate={bidSeller.effectiveDate}
              loading={loading}
              netWorth={bidSeller.nethworth}
              products={bidSeller.products}
              sendDate={bidSeller.sendDate}
              taxation={bidSeller.taxation}
              taxationValue={bidSeller.taxationValue}
              typeCurrency={basicInfo.currency}
            />
            <GridContainer margin="24px 0 48px">
              <GridItem xs={12} md={6} container>
                <GridItem xs={12} sm={4}>
                  <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                    Adjuntos de la cotización
                  </Typography>
                </GridItem>
                <GridItem xs={12} sm>
                  {!loading && bidSeller.attachedFiles && bidSeller.attachedFiles.length > 0 ? (
                    bidSeller.attachedFiles.map(files => (
                      <AttachedFilesBlue
                        id={files.id}
                        key={files.id}
                        name={files.name}
                        onDownloadFile={handleDownloadFile}
                      />
                    ))
                  ) : (
                    <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body2">
                      No existen documentos adjuntos
                    </Typography>
                  )}
                </GridItem>
              </GridItem>
              <CancelBid id={bidSeller.id} onCancelBidShowDialog={handleCancelBidShowDialog} />
            </GridContainer>
          </Card>
        ))}
        <QuoteBidSellerFooter
          disabledSubmit={disabledSubmit}
          margin="48px 0"
          onBackSearch={handleBackSearch}
          onAddOffer={handleAddOffer}
          showButtonPlus
        />
      </GridContainer>
    )
  }

  return (
    <Root>
      <GridContainer padding="60px 0 6px">
        <GridItem container alignItems="center" xs={12}>
          <Typography color="black1" fontSize="22px" fontWeight="bold" lineHeight="26px" variant="h3">
            Participar de la Compra Ágil
          </Typography>
          <Tag color="primary" margin="0 0 0 24px" text={statusCallAgile.call} />
        </GridItem>
      </GridContainer>
      <GridContainer padding="6px 0 0">
        <GridItem xs={12}>
          <Typography color="black1" fontSize="16px" lineHeight="20px" variant="body1">
            Completa la siguiente información y envía tu cotización para participar de esta Compra Ágil.
          </Typography>
        </GridItem>
      </GridContainer>
      <GridContainer padding="20px 0 0">
        <GridItem xs={12}>
          <Divider />
        </GridItem>
        <GridItem xs={12}>
          <Errors errors={!statusCallAgile?.isSecondCall && supplierPrioritized?.messages} severity="primary" />
        </GridItem>
      </GridContainer>
      {!loading && error && (
        <GridContainer margin="16px 0 29px">
          <GridItem xs={12}>
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </GridItem>
        </GridContainer>
      )}
      <GridContainer padding="24px 0 0" spacing={1}>
        <GridItem sm={9} xs={12}>
          {Object.values(productsUpdate).map((product, index) => (
            <QuoteBidSellerProductsList
              disabled={disabledSupplierHability}
              description={product.description}
              id={product.id}
              key={product.id}
              margin={Object.values(productsUpdate).length - 1 !== index ? '0 0 8px' : 0}
              name={product.name}
              quantity={product.quantity}
              setSubtotal={setSubtotal}
              subtotal={product.subtotal}
              typeCurrency={basicInfo.currency}
              unitMeasure={product.unitMeasure}
            />
          ))}
          <QuoteBidSellerDispatchValue
            disabled={disabledSupplierHability}
            areThereProductsIncluded={areThereProductsIncluded}
            calculateDispatchValue={calculateDispatchValue}
            typeCurrency={basicInfo.currency}
          />
        </GridItem>
        <GridItem sm={3} xs={12}>
          <QuoteBidSellerAmount
            disabled={disabledSupplierHability}
            amount={amount}
            netWorth={netWorth}
            percentageTaxation={percentageTaxation}
            taxation={taxation}
            taxationOptions={taxationOptions}
            typeCurrency={basicInfo.currency}
            maxAmountsUTMs={maxAmountsUTMs}
            maximumAmountFormater={maximumAmount}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem sm={9} xs={12}>
          <QuoteBidSellerDetails
            setDescription={setDescription}
            setEffectiveDate={setEffectiveDate}
            disabled={disabledSupplierHability}
          />
          {!disabledSupplierHability && (
            <>
              <GridContainer padding="10px 0 0">
                <GridItem xs={12}>
                  <Divider />
                </GridItem>
              </GridContainer>
              <GridContainer padding="24px 0 0">
                <GridItem xs={12}>
                  <AttachFiles
                    disabled={disabledSupplierHability}
                    info="Peso máximo 20MB (doc, docx, xls, xlsx, pdf)"
                    label="documento anexo (opcional)"
                    leftBreakpoints={{ sm: 3 }}
                    loading={loadingFile}
                    rightBreakpoints={{ sm: 6 }}
                    subtitle="Puedes subir archivos que especifiquen tu cotización (opcional)."
                    onUploadFile={handleUploadFile}
                  />
                  <GridContainer padding="20px 0 0">
                    {loadingFile && (
                      <GridContainer>
                        <GridItem align="center" xs={12}>
                          <Spinner />
                        </GridItem>
                      </GridContainer>
                    )}
                    {!loadingFile && attachedFiles && attachedFiles.length > 0 && (
                      <GridContainer>
                        <GridItem sm={8} xs={12}>
                          {attachedFiles.map((files, index) => (
                            <AttachedFiles
                              divider={attachedFiles.length - 1 !== index}
                              id={files.id}
                              key={files.id}
                              name={files.name}
                              onDeleteFile={handleDeleteFile}
                              onDownloadFile={handleDownloadFile}
                            />
                          ))}
                        </GridItem>
                        <GridItem sm={4} xs={12} />
                      </GridContainer>
                    )}
                  </GridContainer>
                </GridItem>
              </GridContainer>
              <Errors errors={errorsQuoteBidSeller} maxUtm={maxAmountsUTMs} maximumAmountFormater={maximumAmount} />
              <Errors errors={errorsSendSellerQuoteDialog} />
              <GridContainer padding="30px 0 0">
                <GridItem xs={12}>
                  <Divider />
                </GridItem>
              </GridContainer>
            </>
          )}
          {basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED && (
            <Toast severity="warning" margin="24px 0">
              Conforme a la nueva normativa de compras públicas, si existen cotizaciones de empresas relacionadas o
              pertenecientes al mismo grupo empresarial en un mismo proceso de compra,{' '}
              <Strong> solo se admitirá la más conveniente, quedando las otras inadmisibles.</Strong>
            </Toast>
          )}
          <QuoteBidSellerFooter
            showButtonSend
            disabledSubmit={disabledSubmit}
            loading={loadingSendSeller || refreshingEmt}
            margin="21px 0 256px"
            onBackSearch={handleBackSearch}
            onSendQuote={handleSendQuote}
          />
        </GridItem>
        <GridItem sm={3} xs={12} />
      </GridContainer>
      <GridItem sm={3} xs={12} />
    </Root>
  )
}

export default QuoteBidSellerContainer
